import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GraphQLErrorCode } from '@this+that/constants';
import { ApprovalDecision, InboxEntry } from '@this+that/models';
import log from 'loglevel';

import { useActiveInboxEntriesSlice } from './useActiveInboxEntriesSlice';
import { useMarkInboxEntryAsResolved } from './useMarkInboxEntryAsResolved';
import { useRespondToFlowApproval as useRespondToFlowApprovalService } from '../../services/flowApproval.service';
import { useUnreadStore } from '../../store';
import { hasGraphQLErrorCode } from '../../utils/graphqlErrors';
import { useCustomToast } from '../useCustomToast';

interface ApprovalToastKeys {
  loadingKey: string;
  successKey: string;
  errorKey: string;
}

function getApprovalToastKeys(decision: ApprovalDecision): ApprovalToastKeys {
  if (decision === ApprovalDecision.APPROVED) {
    return {
      loadingKey: 'doBox.actions.approvingApproval',
      successKey: 'doBox.actions.approveApprovalSuccess',
      errorKey: 'doBox.actions.approveApprovalError',
    };
  }

  return {
    loadingKey: 'doBox.actions.rejectingApproval',
    successKey: 'doBox.actions.rejectApprovalSuccess',
    errorKey: 'doBox.actions.rejectApprovalError',
  };
}

const APPROVAL_NO_LONGER_PENDING_KEY = 'doBox.actions.approvalNoLongerPending';

interface RespondWithoutExecutionParams {
  entryId: string;
  decision: ApprovalDecision;
  markAsResolved: (
    inboxEntryId: string,
    approvalDecision?: ApprovalDecision,
  ) => Promise<boolean>;
}

async function respondWithoutExecution({
  entryId,
  decision,
  markAsResolved,
}: RespondWithoutExecutionParams): Promise<boolean> {
  const success = await markAsResolved(entryId, decision);

  if (!success) {
    throw new Error('respondToFlowApproval: fallback action failed');
  }

  return true;
}

export const useRespondToFlowApproval = () => {
  const { t } = useTranslation();
  const { respondToFlowApproval: respondService } =
    useRespondToFlowApprovalService();
  const { updateInboxEntry, removeInboxEntry } = useActiveInboxEntriesSlice();
  const { markAsResolved } = useMarkInboxEntryAsResolved();
  const { promiseToast } = useCustomToast();
  const { markAsRead } = useUnreadStore();
  const [isResponding, setIsResponding] = useState(false);

  const respondWithoutExecutionId = useCallback(
    async (entry: InboxEntry, decision: ApprovalDecision): Promise<boolean> => {
      setIsResponding(true);

      const { loadingKey, successKey, errorKey } =
        getApprovalToastKeys(decision);

      const respondPromise = (async () => {
        try {
          return await respondWithoutExecution({
            entryId: entry.id,
            decision,
            markAsResolved,
          });
        } catch (error) {
          log.error('respondToFlowApproval fallback error', {
            entryId: entry.id,
            decision,
            error,
          });
          throw error;
        } finally {
          setIsResponding(false);
        }
      })();

      const didRespond = await promiseToast(
        respondPromise,
        t(loadingKey),
        t(successKey),
        t(errorKey),
      );

      return didRespond === true;
    },
    [markAsResolved, promiseToast, t],
  );

  const respondToFlowApproval = useCallback(
    async (entry: InboxEntry, decision: ApprovalDecision): Promise<boolean> => {
      const executionId = entry.flowApproval?.executionId;

      if (!executionId) {
        return respondWithoutExecutionId(entry, decision);
      }

      setIsResponding(true);

      const loadingKey =
        decision === ApprovalDecision.APPROVED
          ? 'doBox.actions.approvingApproval'
          : 'doBox.actions.rejectingApproval';
      const successKey =
        decision === ApprovalDecision.APPROVED
          ? 'doBox.actions.approveApprovalSuccess'
          : 'doBox.actions.rejectApprovalSuccess';
      const errorKey =
        decision === ApprovalDecision.APPROVED
          ? 'doBox.actions.approveApprovalError'
          : 'doBox.actions.rejectApprovalError';

      const respondPromise = (async () => {
        try {
          await respondService(executionId, decision, entry.id);
          removeInboxEntry(entry.id);
          markAsRead(entry.id, entry.taskGroupId);
          return true;
        } catch (error) {
          updateInboxEntry(entry);
          log.error('respondToFlowApproval error', {
            entryId: entry.id,
            decision,
            error,
          });
          throw error;
        } finally {
          setIsResponding(false);
        }
      })();

      const didRespond = await promiseToast(
        respondPromise,
        t(loadingKey),
        t(successKey),
        (error) =>
          hasGraphQLErrorCode(error, GraphQLErrorCode.CONFLICT)
            ? t(APPROVAL_NO_LONGER_PENDING_KEY)
            : t(errorKey),
      );

      return didRespond === true;
    },
    [
      respondService,
      updateInboxEntry,
      removeInboxEntry,
      promiseToast,
      t,
      markAsRead,
      respondWithoutExecutionId,
    ],
  );

  return { respondToFlowApproval, isResponding };
};
